import type { MatchSettings } from '../sim/rules/settings';
import { MAX_PLAYERS } from '../core/players';
import { choiceControl } from './controls';

export type PartyOptions = Pick<MatchSettings, 'mode' | 'mutators' | 'teams' | 'botStyles' | 'modeScoreLimit' | 'teamStocks' | 'rouletteSeconds'>;
export const MODE_CHOICES = [
  { value: 'classic', label: 'Free-for-all' },
  { value: 'arms-race', label: 'Arms Race' },
  { value: 'office-brawl', label: 'Office Brawl' },
  { value: 'teams', label: 'Teams' },
  { value: 'king-of-hill', label: 'King of the hill' },
  { value: 'weapon-roulette', label: 'Weapon roulette' },
  { value: 'shared-stock', label: 'Shared stock' },
] as const;
export const MODE_HINTS: Record<PartyOptions['mode'], string> = {
  classic: 'Last fighter standing takes the round.',
  'arms-race': 'One arena · 2 kills per gun · Final blade wins. Race through 12 weapons. Respawn with your progress intact.',
  'office-brawl': 'Overtime turns into a brawl. Fight with staplers, chairs, monitors, keyboards and coffee mugs. Last fighter standing wins.',
  teams: 'Sun versus Moon. Protect your teammate; friendly attacks do no damage.',
  'king-of-hill': 'Hold the marked hill to score. Knocked-out fighters respawn. A contested hill scores nothing.',
  'weapon-roulette': 'Everyone gets the same weapon. The timer deals a fresh one to the whole arena.',
  'shared-stock': 'Each team shares respawns. When those run out, hold block beside a fallen teammate to rescue them.',
};
export const MUTATOR_CHOICES = [
  { value: 'low-gravity', label: 'Low gravity' },
  { value: 'giant-knockback', label: 'Giant knockback' },
  { value: 'melee-only', label: 'Melee only' },
  { value: 'same-weapon', label: 'Same loadout' },
] as const;

export function partyOptions(raw: Partial<PartyOptions> = {}): PartyOptions {
  const num = (value: unknown, fallback: number, min: number, max: number) => typeof value === 'number' && Number.isFinite(value) ? Math.round(Math.max(min, Math.min(max, value))) : fallback;
  return {
    mode: MODE_CHOICES.some((m) => m.value === raw.mode) ? raw.mode! : 'classic',
    mutators: Array.isArray(raw.mutators) ? [...new Set(raw.mutators.filter((v) => MUTATOR_CHOICES.some((m) => m.value === v) && (!['arms-race', 'office-brawl'].includes(raw.mode ?? '') || !['melee-only', 'same-weapon'].includes(v))))] : [],
    teams: Array.isArray(raw.teams) ? raw.teams.slice(0, MAX_PLAYERS).map((v, i) => v === 0 || v === 1 ? v : i % 2) : [],
    botStyles: Array.isArray(raw.botStyles) ? raw.botStyles.slice(0, MAX_PLAYERS).filter((v) => ['aggressive', 'defensive', 'opportunistic'].includes(v)) : [],
    modeScoreLimit: num(raw.modeScoreLimit, 20, 5, 120),
    teamStocks: num(raw.teamStocks, 4, 1, 12),
    rouletteSeconds: num(raw.rouletteSeconds, 12, 5, 60),
  };
}

export function partySummary(options: PartyOptions): string {
  const mode = MODE_CHOICES.find((m) => m.value === options.mode)?.label ?? 'Free-for-all';
  return [mode, ...MUTATOR_CHOICES.filter((m) => options.mutators.includes(m.value)).map((m) => m.label)].join(' · ');
}

/** Shared visible choices for Quick Match, Local Play, Settings and the host's room. */
export function partyControls(initial: PartyOptions, prefix: string, onChange: (options: PartyOptions) => void, config: { advanced?: boolean; slots?: string[] } = {}): HTMLElement {
  const current = partyOptions(initial);
  const root = document.createElement('section');
  root.className = 'party-options';
  root.setAttribute('aria-label', 'Party rules');
  const stored = document.createElement('input');
  stored.type = 'hidden';
  stored.id = `${prefix}-party-options`;
  const save = () => {
    stored.value = JSON.stringify(current);
    onChange(partyOptions(current));
    stored.dispatchEvent(new Event('change', { bubbles: true }));
  };
  stored.value = JSON.stringify(current);
  const description = document.createElement('p');
  description.className = 'dim party-description';
  description.textContent = MODE_HINTS[current.mode];
  const teams = document.createElement('div');
  teams.className = 'party-teams';
  const loadoutHint = document.createElement('p');
  loadoutHint.className = 'dim party-loadout-hint';
  loadoutHint.textContent = 'Arms Race uses a fixed weapon ladder. Melee only, Same loadout and weapon drop choices do not apply.';
  const updateTeams = () => {
    teams.hidden = !['teams', 'shared-stock'].includes(current.mode);
    description.textContent = MODE_HINTS[current.mode];
    loadoutHint.hidden = !['arms-race', 'office-brawl'].includes(current.mode);
    loadoutHint.textContent = current.mode === 'office-brawl'
      ? 'Office Brawl uses the Overtime office and its equipment. Attack swings, fires or launches the item you are holding. Arena and weapon drop choices do not apply.'
      : 'Arms Race uses a fixed weapon ladder. Melee only, Same loadout and weapon drop choices do not apply.';
    current.mutators = partyOptions(current).mutators;
    for (const button of root.querySelectorAll<HTMLButtonElement>('[data-mutator]')) {
      button.disabled = ['arms-race', 'office-brawl'].includes(current.mode) && ['melee-only', 'same-weapon'].includes(button.dataset.mutator!);
      button.setAttribute('aria-pressed', String(current.mutators.includes(button.dataset.mutator as PartyOptions['mutators'][number])));
    }
    for (const row of root.querySelectorAll<HTMLElement>('[data-for-mode]')) row.hidden = row.dataset.forMode !== current.mode;
  };
  root.append(stored, choiceControl({ id: `${prefix}-mode`, label: 'Game mode', choices: [...MODE_CHOICES], value: current.mode, onChange: (value) => {
    current.mode = value as PartyOptions['mode']; updateTeams(); save();
  } }), description);
  const mutators = document.createElement('fieldset');
  mutators.className = 'choice-row mutator-options';
  const legend = document.createElement('legend');
  legend.textContent = 'Mix in mutators';
  const row = document.createElement('div');
  row.className = 'choice-options';
  for (const choice of MUTATOR_CHOICES) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'choice-option';
    button.dataset.mutator = choice.value;
    button.textContent = choice.label;
    button.setAttribute('aria-pressed', String(current.mutators.includes(choice.value)));
    button.addEventListener('click', () => {
      const enabled = !current.mutators.includes(choice.value);
      current.mutators = enabled ? [...current.mutators, choice.value] : current.mutators.filter((m) => m !== choice.value);
      button.setAttribute('aria-pressed', String(enabled)); save();
    });
    row.append(button);
  }
  mutators.append(legend, row);
  root.append(mutators, loadoutHint);
  const slots = config.slots ?? Array.from({ length: MAX_PLAYERS }, (_, slot) => `P${slot + 1}`);
  for (let slot = 0; slot < slots.length; slot++) {
    teams.append(choiceControl({ id: `${prefix}-team-${slot}`, label: `${slots[slot]} team`, value: current.teams[slot] ?? slot % 2,
      choices: [{ value: 0, label: 'Sun' }, { value: 1, label: 'Moon' }], onChange: (value) => {
        current.teams = Array.from({ length: MAX_PLAYERS }, (_, i) => current.teams[i] ?? i % 2);
        current.teams[slot] = Number(value); save();
      } }));
  }
  const teamHint = document.createElement('p');
  teamHint.className = 'dim';
  teamHint.textContent = 'At least one fighter is needed on each team. Empty teams are balanced when the match starts.';
  teams.append(teamHint);
  root.append(teams);
  updateTeams();
  if (config.advanced) {
    const style = current.botStyles.length && current.botStyles.every((s) => s === current.botStyles[0]) ? current.botStyles[0]! : 'mixed';
    root.append(choiceControl({ id: `${prefix}-bot-style`, label: 'Bot personalities', value: style,
      choices: [{ value: 'mixed', label: 'Mixed' }, { value: 'aggressive', label: 'Aggressive' }, { value: 'defensive', label: 'Defensive' }, { value: 'opportunistic', label: 'Opportunistic' }],
      hint: 'Aggressive bots push forward, defensive bots keep their distance, opportunists seek vulnerable targets.',
      onChange: (value) => { current.botStyles = value === 'mixed' ? [] : Array(MAX_PLAYERS).fill(value) as PartyOptions['botStyles']; save(); } }));
    for (const [key, label, values, unit] of [
      ['modeScoreLimit', 'Hill control to win a round', [10, 20, 30, 60], 'seconds'],
      ['teamStocks', 'Respawns per team', [2, 4, 6, 8], 'respawns'],
      ['rouletteSeconds', 'Roulette swaps every', [5, 12, 20, 30], 'seconds'],
    ] as const) {
      const choices = [...new Set<number>([...values, current[key]])].sort((a, b) => a - b).map((value) => ({ value, label: `${value} ${unit}` }));
      const control = choiceControl({ id: `${prefix}-${key}`, label, value: current[key], choices, onChange: (value) => { current[key] = Number(value); save(); } });
      control.dataset.forMode = key === 'modeScoreLimit' ? 'king-of-hill' : key === 'teamStocks' ? 'shared-stock' : 'weapon-roulette';
      root.append(control);
    }
  }
  updateTeams();
  return root;
}
